import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useNotify } from 'react-admin'
import { Container, TextField, Button, Typography, Box, Alert, InputAdornment, IconButton } from '@mui/material'
import { supabaseClient as supabase } from './supabase'

import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

const ResetPasswordPage = () => {
  const notify = useNotify()
  const navigate = useNavigate()
  const location = useLocation()
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [error, setError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordConfirmError, setPasswordConfirmError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const token = new URLSearchParams(location.search).get('2C4-P6B_4LQ75YM')

  useEffect(() => {
    if (!token) navigate('/login')
  }, [token, navigate])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)
    setError('')
    setPasswordError('')
    setPasswordConfirmError('')

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{12,}$/.test(password)) {
      setPasswordError('Le mot de passe doit contenir au moins 12 caractères, des lettres minuscules et majuscules, des chiffres, et au moins un caractère spécial.')
      setLoading(false)
      return
    }

    if (passwordConfirm !== password) {
      setPasswordConfirmError('Les mots de passe ne correspondent pas.')
      setLoading(false)
      return
    }

    try {
      const { error: tokenError } = await supabase.auth.verifyOtp({ token_hash: token, type: 'email' })

      if (tokenError) {
        setError('Accès invalide ou expiré. Veuillez contacter un responsable.')
        console.error('Error validating token:', tokenError.message)
        setLoading(false)
        return
      }

      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        setError(error.message)
        return
      }

      notify('Votre mot de passe a été modifié avec succès', { type: 'success' })
      navigate('/login')
    } catch (error) {
      console.error('Error updating password:', error.message)
      setError('Une erreur est survenue. Veuillez contacter un responsable.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth="sm" sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'top',
      alignItems: 'center',
      height: '100vh',
      paddingTop: '10vh',
      backgroundColor: '#f4f4f4',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    }}>
      <Typography variant="h2" sx={{ fontWeight: 900 }} gutterBottom>Forma Contacts</Typography>

      <Typography variant="h5" gutterBottom>
        Réinitialisez votre mot de passe
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          label="Nouveau mot de passe"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value)
            if (passwordError) setPasswordError('')
          }}
          error={!!passwordError}
          helperText={passwordError}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label="Confirmer le mot de passe"
          type={showPassword ? 'text' : 'password'}
          value={passwordConfirm}
          onChange={(e) => {
            setPasswordConfirm(e.target.value)
            if (passwordConfirmError) setPasswordConfirmError('')
          }}
          error={!!passwordConfirmError}
          helperText={passwordConfirmError}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        { !error && (
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading || !!passwordError || !!passwordConfirmError}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? 'Enregistrement...' : 'Continuer'}
          </Button>
        )}
      </Box>
    </Container>
  )
}

export default ResetPasswordPage
